<template>
<m-pageframe
    :title="title"
>
<v-container
    fluid
>
    <v-row dense>
        <v-col>
            <v-card>
                <v-card-title>Message Patterns</v-card-title>
                <v-card-text>
                    <ul class="text-body-1">
                        <li
                            v-for="(pattern, index) in bulk_message_status.message_patterns"
                            :key="index"
                        >
                            {{ pattern }}
                        </li>
                    </ul>
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>

    <v-row dense>
        <v-col>
            <v-card>
                <v-card-title>Message Status</v-card-title>
                <v-card-text class="font-weight-medium text-h6">
                    {{ status_description }}
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>

    <v-row dense>
        <v-col>
            <v-card>
                <v-card-title>Pending</v-card-title>
                <v-card-text class="font-weight-medium text-h6">
                    {{ this.$rc.pretty(bulk_message_status.count_pending, 'int') }}
                </v-card-text>
            </v-card>
        </v-col>
        <v-col>
            <v-card>
                <v-card-title>Sent</v-card-title>
                <v-card-text class="font-weight-medium text-h6">
                    {{ this.$rc.pretty(bulk_message_status.count_sent, 'int') }}
                </v-card-text>
            </v-card>
        </v-col>
        <v-col>
            <v-card>
                <v-card-title>Not Sent</v-card-title>
                <v-card-text class="font-weight-medium text-h6">
                    {{ this.$rc.pretty(not_sent_total, 'int') }}
                </v-card-text>
            </v-card>
        </v-col>
    </v-row>

    <v-row dense>
        <v-col>
            <v-card>
                <v-card-title>Not Sent Detail</v-card-title>
                <v-card-subtitle v-if="filtered_total > 0">
                    Want to resend this bulk message to recipients who were filtered out? <a href="https://redchirp.com/how-to-send-a-bulk-message-to-previously-filtered-recipients/" target="_blank">Learn how here</a>.
                </v-card-subtitle>
                
                <v-simple-table>
                    <template v-slot:default>
                        <thead>
                            <tr>
                                <th class="text-left">
                                    Recipient Status
                                </th>
                                <th class="text-left">
                                    Recipient Count
                                </th>
                                <th class="text-center">
                                    Description
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <recipient-table-row
                                label='Cancelled'
                                :value="bulk_message_status.count_cancelled"
                                description="Sending was cancelled before these recipients were sent"
                            ></recipient-table-row>

                            <recipient-table-row
                                label='Failed'
                                v-if="failed_total > 0"
                            ></recipient-table-row>
                                <recipient-table-row
                                    label='Unable to Generate Message'
                                    :value="bulk_message_status.count_failed_unable_to_generate_message"
                                    description="Unable to generate the message text to send from the Message Patterns provided"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Other'
                                    :value="bulk_message_status.count_failed_other"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Failed Total'
                                    :value="failed_total"
                                    indented
                                    additional_classes="font-weight-medium"
                                ></recipient-table-row>

                            <recipient-table-row
                                label='Filtered'
                                v-if="filtered_total > 0"
                            ></recipient-table-row>
                                <recipient-table-row
                                    label='Consent'
                                    :value="bulk_message_status.count_filtered_consent"
                                    description="No express written consent on file in RedChirp for these types of bulk messages"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Days Since Last Bulk Message'
                                    :value="bulk_message_status.count_filtered_days_since_last_bulk_message"
                                    description="Recipient received another bulk message within the configured number of days (configured in Bulk Message Settings)"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Duplicate'
                                    :value="bulk_message_status.count_filtered_duplicate"
                                    description="A message was already sent to the recipient as part of this bulk message"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Order History'
                                    :value="bulk_message_status.count_filtered_order_history"
                                    description="Recipient has not placed an order within the configured number of months (configured in Bulk Message Settings)"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Previously Opted Out'
                                    :value="bulk_message_status.count_filtered_previously_opted_out"
                                    description="Recipient previously opted out of these types of bulk messages"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Received Other Bulk Message(s)'
                                    :value="bulk_message_status.count_filtered_received_bulk_message"
                                    description="Recipient already received a related bulk message (configured in Bulk Message Settings)"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Send Time Restriction'
                                    :value="bulk_message_status.count_filtered_send_time_restrictions"
                                    description="Send attempted during a restricted time, e.g. holiday (configured in Bulk Message Settings)"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Simple Do Not Send After'
                                    :value="bulk_message_status.count_filtered_simple_do_not_send_after_wall_time"
                                    description="Send attempted after the 'avoid sending after' time specified in this bulk message (configured in Bulk Message Settings)"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='TCPALitigatorList.com'
                                    :value="bulk_message_status.count_filtered_tcpa_litigators_list"
                                    description="Recipient phone number appears in a database of known litigants and do not call list complainers"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Other'
                                    :value="bulk_message_status.count_filtered_other"
                                    indented
                                ></recipient-table-row>
                                <recipient-table-row
                                    label='Filtered Total'
                                    :value="filtered_total"
                                    indented
                                    additional_classes="font-weight-medium"
                                ></recipient-table-row>

                            <recipient-table-row
                                label='Total'
                                :value="not_sent_total"
                                additional_classes="font-weight-bold row-total"
                            ></recipient-table-row>
                        </tbody>
                    </template>
                </v-simple-table>
            </v-card>
        </v-col>
    </v-row>

    <v-row dense>
        <v-col>
            <v-card>
                <v-card-title>Links</v-card-title>
                <v-card-actions>
                    <m-list-buttons
                        :items="links_buttons"
                    />
                </v-card-actions>
            </v-card>
        </v-col>
    </v-row>
</v-container>
</m-pageframe>
</template>

<script>
import RecipientTableRow from './view-bulk-message-send-summary-recipient-table-row.vue';

export default {

    data() {
        return {
            bulk_message_status: {}, // Empty object avoids a bunch of null errors in row component bindings

            // TODO Conditional links?
            links_buttons: [
                { 'title': 'Share', 'sub_title': 'Share this link with fellow RedChirp users in your account', 'icon': 'mdi-share-outline', 'action': this.onShare },
                { 'title': 'Recipients Detail (Excel)', 'sub_title': 'Export to Excel with all recipients and their current status', 'icon': 'mdi-file-excel-outline', 'action': this.onExportRecipientDetail },
                { 'title': 'Order Attribution', 'sub_title': 'View analytics for orders attributed to this bulk message', 'icon': 'mdi-google-analytics', 'action': this.linkToOrderAttribution },
                { 'title': 'Bulk Message Settings', 'sub_title': 'View settings for this bulk message', 'icon': 'mdi-comment-text-multiple', 'action': this.linkToBulkMessageSettings },
                { 'title': 'Change Log', 'sub_title': 'View account change log entries for this bulk message', 'icon': 'mdi-format-list-bulleted-square', 'action': this.linkToChangeLog },
            ],
        }
    },

    mounted () {
        this.update();
    },

    components: {
        RecipientTableRow
    },

    methods: {
        update() {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'GET-PROFILE: /api/v1/bulk/messages/send-status-detail/:bulk_message_id',
            
                'params' : {
                    'bulk_message_id': this.$route.params.bulk_message_id
                },
            
                'on_success': (data) => {
                    this.bulk_message_status = data.bulk_message_status;

                    if (!this.sendComplete)
                    {
                        window.setTimeout(this.update, 5000);
                    }
                },
            
                // Only alert on initial page load
                'on_error': this.bulk_message_status?.bulk_message_id ? null : 'Unable to get bulk message status',
            });
        },

        onShare() {
            this.$rc.rest({
                'spinner': true,
                'endpoint': 'LIST: /api/v1/organizations/employees/labels',

                'params': {
                    'organization_id' : this.bulk_message_status.organization_id,
                },

                'on_success': (data) => {
                    this.pickShareRecipients(data.results);
                },

                'on_error': 'Unable to get employee list'
            });
        },

        pickShareRecipients(employees) {
            let choices = employees
                .filter(e => !e.is_archived)
                .map(e => {
                    return {
                        'text': e.employee_label,
                        'value': e.employee_id,
                    };
                }
            );

            this.$rc.pick({
                'title': 'Users to Share With',
                'content_before': null,
                'content_after': 'Selected users will get a text and/or email with a link to this page',
            
                'choices': choices,
            
                'multiple': true,
                'type': 'select',
                'select_label': 'Select Users',
                'initial_value': null,
                'unset_label': null,
            
                'on_ok': (value) => {
                    this.doShareWithRecipients(value);
                },
            
                'on_cancel': () => {}
            });
        },

        doShareWithRecipients(employee_ids) {
            this.$rc.rest({
                'spinner': false,
                'endpoint': 'PUT: /api/v1/bulk/messages/send-status-detail/share',

                'params': {
                    'organization_id': this.bulk_message_status.organization_id,
                    'bulk_message_id': this.bulk_message_status.bulk_message_id,
                    'employee_ids': employee_ids,
                },

                'on_success': (data) => {
                    this.$rc.snackbar('Sharing ' + (1 == employee_ids.length ? 'message' : 'messages') + ' sent');
                },

                'on_error': 'Unable to share'
            });
        },

        onExportRecipientDetail() {
            this.$rc.excelExport(
                'EXPORT: /api/v1/bulk/recipients/export/:organization_id/:bulk_message_id',
                {
                    'organization_id': this.bulk_message_status.organization_id,
                    'bulk_message_id': this.bulk_message_status.bulk_message_id,
                }
            );
        },

        linkToOrderAttribution() {
            let route = {
                'name': 'analytics-one-bulk-message-analytics-group',
                'params' : {
                    'organization_id': this.bulk_message_status.organization_id,
                    'analytics_group_id': this.bulk_message_status.comp_analytics_group_id,
                }
            };

            let resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        },

        linkToBulkMessageSettings() {
            let route = {
                'name': 'view-bulk-message',
                'params' : {
                    'organization_id' : this.bulk_message_status.organization_id,
                    'bulk_message_id' : this.bulk_message_status.bulk_message_id,
                }
            };

            let resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        },

        linkToChangeLog() {
            let route = {
                'name': 'account-change-log',
                'params' : {
                    'organization_id' : this.bulk_message_status.organization_id
                },
                'query': {
                    'subject_uuid': this.bulk_message_status.bulk_message_id
                } 
            };

            let resolved_route = this.$router.resolve(route);
            window.open(resolved_route.href, '_blank');
        }
    },

    computed: {
        sendComplete() {
            switch (this.bulk_message_status?.state)
            {
                case 'edit': return false;
                case 'send': return false;
                case 'paused': return false;
                case 'finished': return true;
                case 'cancelled': return true;
            }

            return false;
        },

        title() {
            if (this.bulk_message_status?.label)
            {
                return "Bulk Message Status: " + this.bulk_message_status.label;
            }
            
            return "Bulk Message Status";
        },

        status_description() {
            switch (this.bulk_message_status?.state)
            {
                case 'edit':
                {
                    if (this.bulk_message_status.scheduled_send_time && this.bulk_message_status.scheduled_send_time > 0)
                    {
                        let date = this.$rc.pretty(this.bulk_message_status.scheduled_send_time, 'date');
                        let time = this.$rc.pretty(this.bulk_message_status.scheduled_send_time, 'time');
                        let employee = this.bulk_message_status.scheduled_by_employee_label;

                        return `Scheduled to be sent on ${date} at ${time} by ${employee}`;
                    }

                    return 'Not Yet Sent';
                }
                case 'send': return 'Sending';
                case 'paused': return 'Paused';
                case 'finished': return 'Finished Sending';
                case 'cancelled': return 'Cancelled';
            }

            return 'Unknown';
        },

        failed_total() {
            if (! this.bulk_message_status) return 0;

            return this.bulk_message_status.count_failed_other
                 + this.bulk_message_status.count_failed_unable_to_generate_message;
        },

        filtered_total() {
            if (! this.bulk_message_status) return 0;

            return this.bulk_message_status.count_filtered_duplicate
                 + this.bulk_message_status.count_filtered_previously_opted_out
                 + this.bulk_message_status.count_filtered_consent
                 + this.bulk_message_status.count_filtered_simple_do_not_send_after_wall_time
                 + this.bulk_message_status.count_filtered_send_time_restrictions
                 + this.bulk_message_status.count_filtered_days_since_last_bulk_message
                 + this.bulk_message_status.count_filtered_received_bulk_message
                 + this.bulk_message_status.count_filtered_order_history
                 + this.bulk_message_status.count_filtered_tcpa_litigators_list
                 + this.bulk_message_status.count_filtered_other;
        },

        not_sent_total() {
            if (! this.bulk_message_status) return 0;

            return this.bulk_message_status.count_cancelled
                 + this.failed_total
                 + this.filtered_total;
        },

        recipients_total() {
            if (! this.bulk_message_status) return 0;

            return this.bulk_message_status.count_pending
                 + this.bulk_message_status.count_sent
                 + this.not_sent_total;
        }
    }
}


</script>

<style lang="scss" scoped>
</style>